<template>
	<div class="map-input-container">
		<div class="row centered flex-end input-container">
			<div class="row centered address-input-row">
				<AppTextInput
					ref="text-input"
					:placeholder="placeholder"
					:label="label"
					:value="value"
					:name="name"
					:class="{disabled: !ready}"
					class="address-input"
					@focusout-input="focusOutInput"
					@click="clickInput"
				/>
				<AppClickableIcon
					v-if="canEditFields"
					icon-name="iconEdit"
					class="edit-fields-icon"
					:auto-set-active="false"
					@click-icon="openEditFields"
				/>
			</div>
			<div class="second-row" :class="{showSecondRow: showCivic || showZip}">
				<AppTextInput
					ref="street-number-input"
					:class="{showCivic}"
					class="input-optional"
					:label="getString('civic')"
					:placeholder="getString('civic')"
					@changed-text="changedCivic"
				/>
				<AppTextInput
					ref="zip-number-input"
					:class="{showZip}"
					class="input-optional"
					:label="getString('zip')"
					:placeholder="getString('zip')"
					@changed-text="changedZip"
				/>
			</div>
		</div>
		<AppInPlaceCloud @close-cloud="closeCloud" @click="clickMap" ref="maps-cloud" :active="!noMap && activeMap">
			<div ref="map-container" class="cloud-map-container"></div>
		</AppInPlaceCloud>
		<AppAddressFieldsPopup
			ref="edit-fields-popup"
			:address-data="currentData"
			@confirm-fields="confirmEditFields"
		/>
	</div>
</template>

<script>
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppInPlaceCloud from "@/shared/components/AppInPlaceCloud.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import AppAddressFieldsPopup from "@/shared/components/AppAddressFieldsPopup.vue";

import {useLanguageStore, useScriptStore} from "@/shared/stores";

export default {
	name: "AppGoogleMapsInput",
	emits: ['changed-address'],
	components: {AppTextInput, AppInPlaceCloud, AppClickableIcon, AppAddressFieldsPopup},
	setup() {
		const scriptStore = useScriptStore();
		const languageStore = useLanguageStore();
		return {loadScript: scriptStore.loadScript, getString: languageStore.getString};
	},
	data() {
		return {
			//expandedStreetNumber: false,
			address: null,
			map: null,
			autocomplete: null,
			marker: null,
			activeMap: false,
			ready: false,
			showCivic: false,
			showZip: false,
			mapOptions: {
				mapTypeControl: false,
				panControl: false,
				zoomControl: false,
				streetViewControl: false
			},
			autocompleteOptions: {
				types: ["address"],
				componentRestrictions: {country: COUNTRY}
			},
			autocompleteFields: ["formatted_address", "address_components", "geometry"],
			closeMapListener: null,
			currentData: null
		}
	},
	props: {
		placeholder: {type: String, default: ""},
		label: {type: String, default: ""},
		value: {type: String, default: ""},
		noMap: {type: Boolean, default: false},
		name: {type: String, default: null},
		countryRestriction: {type: String, default: null},
		manualEditable: {type: Boolean, default: true},
	},
	computed: {
		// we mandatorily need its lat/lng before allowing a manual save.
		canEditFields() {
			return this.manualEditable && !!(this.currentData && this.currentData.lat && this.currentData.lng);
		},
	},
	methods: {
		initMapsInput() {
			this.map = new google.maps.Map(this.$refs['map-container'], this.mapOptions);
			let input = this.$refs['text-input'].$refs['input'];
			let autocompleteOptions = this.autocompleteOptions;
			if(this.countryRestriction && this.countryRestriction !== 'OO') {
				autocompleteOptions.componentRestrictions.country = this.countryRestriction;
			} else if(this.countryRestriction === 'OO'){
				autocompleteOptions.componentRestrictions = null;
			}
			this.autocomplete = new google.maps.places.Autocomplete(input, autocompleteOptions);
			this.autocomplete.setFields(this.autocompleteFields);
			this.initMap(addressData=>{
				this.$emit('changed-address', addressData);
			})
		},
		hasNecessaryFields(addressData) {
			let basic = addressData.via && addressData.provincia;
			if(!basic) {
				return false;
			}
			if(!addressData.civico) {
				if(this.$refs['street-number-input'] && this.$refs['street-number-input'].currentValue && this.$refs['street-number-input'].currentValue.length > 0) {
					addressData.civico = this.$refs['street-number-input'].currentValue;
				} else {
					return false;
				}
			}
			if(!addressData.zip) {
				if(this.$refs['zip-number-input'] && this.$refs['zip-number-input'].currentValue && this.$refs['zip-number-input'].currentValue.length > 0) {
					addressData.zip = this.$refs['zip-number-input'].currentValue;
				} else {
					return false;
				}
			}
			return addressData;
		},
		initMap(addressDataCallback) {

			google.maps.event.addListener(this.autocomplete, 'place_changed', ()=>{
		
				let place = this.autocomplete.getPlace();
		
				if(place && place.geometry) {

					let lat = place.geometry.location.lat();
					let lng = place.geometry.location.lng();
					this.setMapPosition(lat, lng);
		
					let address = place.formatted_address;
					let addressData = this.addressDataFromPlace(place);

					this.activeMap = true;
					this.address = address;

					this.showCivic = !addressData.civico;
					this.showZip = !addressData.zip;
					addressData.lat = lat;
					addressData.lng = lng;
					this.currentData = this.hasNecessaryFields(addressData);
					if(this.currentData) {
						addressDataCallback({
							address: address,
							lat: lat,
							lng: lng,
							data: this.currentData
						});
					} else {
						this.currentData = addressData;
					}
				} else {
					this.address = null;
				}
		
			});
		},
		changedCivic(v) {
			if(this.currentData && v.length > 0 && v.length <= 10) {
				let newAddressData = this.hasNecessaryFields(this.currentData);
				if(newAddressData) {
					this.$emit('changed-address', {
						address: this.address,
						lat: newAddressData.lat,
						lng: newAddressData.lng,
						data: newAddressData
					});
				}
			}
		},
		changedZip(v) {
			if(this.currentData && v.length === 5) {
				let newAddressData = this.hasNecessaryFields(this.currentData);
				if(newAddressData) {
					this.$emit('changed-address', {
						address: this.address,
						lat: newAddressData.lat,
						lng: newAddressData.lng,
						data: newAddressData
					});
				}
			}
		},
		resetMarker(lat, lng) {
			let latLng = new google.maps.LatLng(lat, lng);
			if(!this.marker)  {
				this.marker = new google.maps.Marker({animation: google.maps.Animation.DROP});
				this.marker.setMap(this.map);
			}
			this.marker.setPosition(latLng);
		},
		setMapPosition(lat, lng) {
			if(this.ready && this.map && lat && lng) {
				let latLng = new google.maps.LatLng(lat, lng);
				this.map.panTo(latLng);
				this.map.setZoom(16);
				this.resetMarker(lat, lng);
			}
		},
		addressDataFromPlace(place) {
			let returningData = {};
			let locality = null;
			place.address_components.forEach(component => {

				if(component.types && component.types.length > 0) {
					if(component.types[0] === "administrative_area_level_1") {
						returningData.regione = component.long_name;
						if(this.countryRestriction && this.countryRestriction !== 'IT') {
							if(!returningData?.provincia_codice) {
								returningData.provincia_codice = component.short_name;
							}
						}
					} else if(component.types[0] === "administrative_area_level_2") {
						returningData.provincia_codice = component.short_name;
						returningData.provincia = component.long_name;
					} else if(component.types[0] === "administrative_area_level_3") {
						returningData.comune = component.long_name;
					} else if(component.types[0] === "locality") {
						locality = component.long_name;
					} else if(component.types[0] === "route") {
						returningData.via = component.long_name;
					} else if(component.types[0] === "postal_code") {
						returningData.zip = component.long_name;
					} else if(component.types[0] === "street_number") {
						returningData.civico = component.long_name;
					} else if(component.types[0] === "country") {
						returningData.country = component.short_name;
					}
				}

			});
			if(!returningData.country) {
				returningData.country = this.countryRestriction || COUNTRY;
			}

			if(returningData.country !== 'IT') {
				returningData.provincia = returningData.provincia || locality || returningData.regione || null;
				returningData.comune = returningData.comune || returningData.provincia || null;
			}

			return returningData;
		},
		clickInput() {
			if(!this.closeMapListener) {
				this.closeMapListener = (e)=>{
					if(e.target && this.$refs && this.$refs['text-input'] && this.$refs['text-input'].$refs && e.target.id !== this.$refs['text-input'].$refs['input'].id && !this.elementIsInCloud(e.target)) {
						this.closeMapListener = null;
						if(this.activeMap) {
							this.activeMap = false;
						}
					} else {
						document.addEventListener('click', this.closeMapListener, {once: true});
					}
				}
				document.addEventListener('click', this.closeMapListener, {once: true});
			}
		},
		closeCloud() {
			this.activeMap = false;
			document.removeEventListener('click', this.closeMapListener);
		},
		elementIsInCloud(element) {
			let maxIterations = 20;
			let currentIterations = 0;
			while (element.parentElement && currentIterations <= maxIterations) {
				if(element === this.$refs['maps-cloud'].$el) {
					return true;
				}
				element = element.parentElement;
				currentIterations++;
			}
			return false;
		},
		openEditFields() {
			if(this.canEditFields && this.$refs['edit-fields-popup']) {
				let inlineCivic = this.$refs['street-number-input']?.currentValue;
				let inlineZip = this.$refs['zip-number-input']?.currentValue;
				if(!this.currentData.civico && inlineCivic) {
					this.currentData.civico = inlineCivic;
				}
				if(!this.currentData.zip && inlineZip) {
					this.currentData.zip = inlineZip;
				}
				this.$refs['edit-fields-popup'].open();
			}
		},
		confirmEditFields({data, address}) {
			this.currentData = data;
			this.address = address;
			// Hide the inline civic/zip fallbacks once those values are set manually.
			this.showCivic = !data.civico;
			this.showZip = !data.zip;
			if(this.$refs['street-number-input']) {
				this.$refs['street-number-input'].currentValue = data.civico || "";
			}
			if(this.$refs['zip-number-input']) {
				this.$refs['zip-number-input'].currentValue = data.zip || "";
			}
			this.$emit('changed-address', {
				address: this.address,
				lat: data.lat,
				lng: data.lng,
				data: data
			});
		},
		focusOutInput() {
			this.activeMap = false;
		},
		clickMap() {
			this.activeMap = true;
		}
	},
	mounted() {
		this.loadScript('maps', ()=>{
			this.ready = true;
			this.initMapsInput();
		});
	},
	watch: {
		countryRestriction(newVal) {
			if(this.autocomplete) {
				let newRestrictions = newVal === 'OO' ? null : {country: newVal || COUNTRY};
				this.autocomplete.setOptions({componentRestrictions: newRestrictions});
			}
		}
	}
}
</script>
<style lang="scss">
.pac-container {
	border-radius: 12px;
	padding-bottom: 5px;

	.pac-item span {
		line-height: 30px;
		font-size: 11px;
		color: #515151;
	}
}
.cloud-map-container {
	height: 180px;
	width: 100%;
	min-width: 260px;
	@media (max-width: 450px) {
		min-width: 200px;
	}
}
</style>
<style lang="scss" scoped>
.map-input-container {
	position: relative;
	max-width: 100%;
	display: flex;
	flex-direction: column;

	> .row { flex-wrap: nowrap;	}
	.input-wrapper {
		min-width: unset;
		transition: width .3s ease;
	}
	.in-place-cloud {
		width: 280px;
		z-index: 10000;
	}

	.input-container {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}

	.address-input-row {
		flex-wrap: nowrap;
		align-items: flex-end;
		justify-content: flex-start;
		gap: 8px;
		width: 280px;
		max-width: 100%;

		.address-input {
			flex: 1 1 auto;
			min-width: 0;
			width: auto;

			&:deep(.input-wrapper) {
				width: 100%;
				min-width: 0;
			}
		}

		.edit-fields-icon {
			flex: 0 0 auto;
			margin-bottom: 12px;
			cursor: pointer;
		}
	}

	.second-row {
		display: none;
		flex-wrap: nowrap;
		justify-content: flex-start;
		align-items: center;
		width: 280px;
		margin-top: 12px;
		gap: 8px;

		&.showSecondRow {
			display: flex;
		}

		.input-optional {
			display: none;
		}

		.showCivic, .showZip {
			display: block;
		}
	}

	@media (max-width: 450px) {
		.input-wrapper, .in-place-cloud {
			width: 220px;
		}
		.address-input-row {
			width: 220px;
		}
	}
}
</style>